import { IGiftPackagingBoxSettings, IFundRaisingSettings } from '@model';

import { IDiscoveryBox, IEcoPackaging, IFundRaising, IGiftPackaging, IInsurance } from '../../model';
import { getImageFromBoxSettings } from '../GiftPackagingModalBody';

import { IGiftPackagingModel } from './model';
import { EcoPackagingState, IEcoPackagingModel } from './model/ecoPackagingModel';
import { IFundRaisingModel } from './model/fundRaisingModel';
import { IInsuranceModel } from './model/insuranceModel';

const getGiftPackagingPrice = (giftPackagingList: IGiftPackaging[], settingsGiftPackagingPrice: number): number => {
  const priceTotal = giftPackagingList.reduce((price, { priceWithVat }) => price + priceWithVat, 0);

  if (!giftPackagingList.length) {
    return settingsGiftPackagingPrice;
  }
  return priceTotal;
};

const getEcoPackagingState = (
  giftPackagingBoxes: number,
  discoveryBoxes: number,
  ecoPackagingOn: boolean,
): EcoPackagingState => {
  if (!ecoPackagingOn) {
    return EcoPackagingState.DISABLED_FEATURE;
  }

  if (giftPackagingBoxes > 0) {
    return EcoPackagingState.DISABLED_GIFT;
  }

  if (discoveryBoxes > 0) {
    return EcoPackagingState.DISABLED_DISCO;
  }

  return EcoPackagingState.ENABLED;
};

const getGiftPackagingImages = (
  giftPackagingList: IGiftPackaging[],
  giftPackagingTypes: IGiftPackagingBoxSettings[],
): { key: string; url: string }[] =>
  giftPackagingList.map((item, index) => {
    const url = getImageFromBoxSettings(giftPackagingTypes, item.productCode);
    return { key: `${index}-${url}`, url: url };
  });

export const isAnyGiftPackagingAvailable = (
  giftPackagingList: IGiftPackaging[],
  giftPackagingTypes: IGiftPackagingBoxSettings[],
  giftPackagingOn: boolean,
): boolean => giftPackagingOn && (giftPackagingList.length > 0 || giftPackagingTypes.some((x) => x.isActive));

/**
 * Creates a view model for gift packaging service component.
 * @param giftPackagingBoxes The gift packaging boxes in the cart.
 * @param giftPackagingTypes The types of gift packaging service.
 * @param settingsGiftPackagingPrice The price of packaging from service.
 * @param ecoPackaging The eco packaging service in the cart.
 * @param giftPackagingOn Displays if gift packaging feature switched is enabled.
 */
export const getGiftPackagingModel = (
  giftPackagingOn: boolean,
  giftPackagingBoxes: IGiftPackaging[],
  giftPackagingTypes: IGiftPackagingBoxSettings[],
  settingsGiftPackagingPrice: number,
  ecoPackaging?: IEcoPackaging | null,
): IGiftPackagingModel => {
  const images = getGiftPackagingImages(giftPackagingBoxes, giftPackagingTypes);
  const unavailable = !isAnyGiftPackagingAvailable(giftPackagingBoxes, giftPackagingTypes, giftPackagingOn);
  const checked = giftPackagingBoxes.length > 0;

  return {
    unavailable: unavailable && !checked,
    images: images,
    price: getGiftPackagingPrice(giftPackagingBoxes, settingsGiftPackagingPrice),
    quantity: images.length > 0 ? images.length : 1,
    checked: checked,
    disabled: (Boolean(ecoPackaging) || unavailable) && !checked,
  };
};

export const getEcoPackagingModel = (
  ecoPackagingOn: boolean,
  giftPackagingBoxes: IGiftPackaging[],
  ecoPackagingPrice: number,
  discoveryBoxes: IDiscoveryBox[],
  ecoPackaging?: IEcoPackaging | null,
): IEcoPackagingModel => {
  return {
    checked: Boolean(ecoPackaging),
    state: getEcoPackagingState(giftPackagingBoxes.length, discoveryBoxes.length, ecoPackagingOn),
    price: ecoPackaging?.priceWithVat ?? ecoPackagingPrice,
  };
};

export const getInsuranceModel = (insurancePrice: number, insurance?: IInsurance | null): IInsuranceModel => {
  return {
    checked: Boolean(insurance),
    price: insurance?.priceWithVat || insurancePrice,
  };
};

export const getFundRaisingModel = (
  fundRaisingSettings: IFundRaisingSettings,
  fundRaising?: IFundRaising | null,
): IFundRaisingModel => ({
  checked: Boolean(fundRaising),
  disabled: !fundRaisingSettings.active,
  price: fundRaising?.priceWithVat || fundRaisingSettings.price,
  image: fundRaisingSettings.image && `images/img/fund-raising/${fundRaisingSettings.image}`,
});
